<template>
  <div
    class="rich-editor"
    :class="{ 'rich-editor--drag': isDragOver }"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop">
    <div
      ref="container"
      class="rich-editor__body"
      :style="{ height }" />
    <div
      v-if="isDragOver"
      class="rich-editor__overlay">
      Отпустите файл — картинка добавится в комментарий
    </div>
  </div>
</template>

<script setup>
import {
  onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { message } from 'ant-design-vue';
import Quill from 'quill';
import Delta from 'quill-delta';
import { sanitizeRichText } from '@/helpers/richText';

import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

const props = defineProps({
  modelValue: { type: String, default: '' },
  // Асинхронная загрузка файла на бэкенд: (File) => Promise<string url>.
  // Без неё редактор остаётся текстовым — картинку вставить не дадим.
  uploadImage: { type: Function, default: null },
  placeholder: { type: String, default: '' },
  height: { type: String, default: '180px' },
});

const emit = defineEmits(['update:modelValue']);

const TOOLBAR = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike', 'link'],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
  ['image'],
];

const container = ref(null);
const isDragOver = ref(false);

let editor = null;
// Последнее значение, о котором редактор и родитель договорились: защищает от циклов v-model.
let lastValue = '';

const editorHtml = () => {
  const isEmpty = editor.getText().trim() === '' && !editor.root.querySelector('img');
  return isEmpty ? '' : editor.root.innerHTML;
};

const emitValue = () => {
  const value = editorHtml();
  if (value === lastValue) return;

  lastValue = value;
  emit('update:modelValue', value);
};

const applyValue = (value) => {
  const next = value ?? '';
  if (next === lastValue) return;

  lastValue = next;

  if (!next) {
    editor.setText('');
    return;
  }

  editor.setContents([]);
  editor.clipboard.dangerouslyPasteHTML(0, sanitizeRichText(next), 'silent');
};

const ensureSelection = () => {
  const selection = editor.getSelection();
  if (selection) return selection;

  editor.setSelection(editor.getLength(), 0);
  return editor.getSelection();
};

// Ошибку показываем словами бэкенда: чаще всего это правила валидации файла (тип, размер).
const uploadErrorText = (error) => {
  const errors = error?.response?.data?.errors;
  if (errors) return Object.values(errors).flat().join(', ');

  return error?.response?.data?.message || error?.message || 'Не удалось загрузить изображение';
};

const handleImageFile = async (file) => {
  if (!file.type.startsWith('image/')) {
    message.warning('Пожалуйста, выберите файл изображения');
    return;
  }

  if (!props.uploadImage) {
    message.warning('Загрузка изображений здесь недоступна');
    return;
  }

  const range = ensureSelection();
  if (!range) return;

  const placeholder = `[Загрузка ${file.name}...]`;
  editor.insertText(range.index, placeholder, 'user');

  try {
    const url = await props.uploadImage(file);
    editor.deleteText(range.index, placeholder.length, 'user');
    editor.insertEmbed(range.index, 'image', url, 'user');
    editor.setSelection(range.index + 1, 0);
  } catch (error) {
    editor.deleteText(range.index, placeholder.length, 'user');
    message.error(uploadErrorText(error));
  }
};

const imageHandler = () => {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.onchange = () => {
    const [file] = input.files;
    if (file) handleImageFile(file);
  };
  input.click();
};

const onPaste = (event) => {
  const items = Array.from(event.clipboardData?.items ?? []);
  const image = items.find((item) => item.type.startsWith('image/'));
  if (!image) return;

  event.preventDefault();
  const file = image.getAsFile();
  if (file) handleImageFile(file);
};

const onDragOver = () => {
  isDragOver.value = true;
};

const onDragLeave = (event) => {
  if (event.currentTarget.contains(event.relatedTarget)) return;
  isDragOver.value = false;
};

const onDrop = (event) => {
  isDragOver.value = false;

  const files = Array.from(event.dataTransfer?.files ?? [])
    .filter((file) => file.type.startsWith('image/'));

  if (!files.length) {
    message.warning('Пожалуйста, перетащите файлы изображений');
    return;
  }

  files.forEach(handleImageFile);
};

// Картинку, вставленную как data:URL внутри html, редактор не принимает: в комментарии
// должна лежать ссылка на файл в хранилище, иначе текст комментария раздувается до мегабайтов.
const rejectInlineImages = (node, delta) => {
  if (node.tagName !== 'IMG') return delta;

  const src = node.getAttribute('src') || '';
  if (!src.startsWith('data:image')) return delta;

  message.warning('Вставьте картинку из буфера обмена или загрузите файлом');
  return new Delta().retain(delta.length());
};

onMounted(() => {
  editor = new Quill(container.value, {
    theme: 'snow',
    placeholder: props.placeholder,
    modules: {
      toolbar: {
        container: TOOLBAR,
        handlers: { image: imageHandler },
      },
      clipboard: {
        matchers: [[Node.ELEMENT_NODE, rejectInlineImages]],
      },
    },
  });

  editor.on('text-change', emitValue);
  editor.root.addEventListener('paste', onPaste);

  applyValue(props.modelValue);
});

onBeforeUnmount(() => {
  editor?.root?.removeEventListener('paste', onPaste);
  editor = null;
});

watch(() => props.modelValue, (value) => {
  if (editor) applyValue(value);
});

defineExpose({
  focus: () => editor?.focus(),
});
</script>

<style scoped>
.rich-editor {
  position: relative;
}
.rich-editor :deep(.ql-toolbar) {
  border-radius: 6px 6px 0 0;
}
.rich-editor :deep(.ql-container) {
  border-radius: 0 0 6px 6px;
  font-size: 14px;
}
.rich-editor :deep(.ql-editor img) {
  max-width: 100%;
}
.rich-editor--drag :deep(.ql-container) {
  border-color: #1890ff;
}
.rich-editor__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #1890ff;
  border-radius: 6px;
  background: rgba(24, 144, 255, 0.08);
  color: #1890ff;
  pointer-events: none;
}
</style>
